


































// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import type { ErrorResponse } from '@apollo/client/link/error';
import { fromPromise } from '@apollo/client/link/utils/fromPromise';
import * as Sentry from '@sentry/vue';
import { useQuery } from '@vue/apollo-composable';
import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import { useEventListener } from '@vueuse/core';
import Background from '@/components/Background.vue';
import DemoBorder from '@/components/DemoBorder.vue';
import DevMenu from '@/components/DevMenu.vue';
import ErrorBoundary from '@/components/ErrorBoundary.vue';
import IdleListener from '@/components/IdleListener.vue';
import ModalError from '@/components/ModalError.vue';
import ModalMessage from '@/components/ModalMessage.vue';
import Scene from '@/components/Scene.vue';
import Symbols from '@/components/SvgSymbols.vue';
import {
  MyKeyboardLayoutDocument,
  MeAuthDocument,
} from '@/graphql/generated/graphql';
import { useNetwork } from '@/hooks/network';
import { refreshCsrf } from '@/lib/apollo/csrf';
import { setGraphqlErrorHandler } from '@/lib/apollo/error';
import { QueueLink } from '@/lib/apollo/queue';
import { APP_VERSION } from '@/lib/env';
import { date } from '@/lib/filters/date';
import { logger } from '@/logger';
import { useAuthStore } from '@/stores/auth';
import { useMainStore } from '@/stores/main';
import { canRetryForever } from './lib/apollo/retry';

export default defineComponent({
  name: 'TtApp',
  components: {
    DemoBorder,
    Background,
    DevMenu,
    Scene,
    Symbols,
    ErrorBoundary,
    IdleListener,
  },
  metaInfo: {
    // if no subcomponents specify a metaInfo.title, this title will be used
    // all titles will be injected into this template
    titleTemplate: 'Typetuin - %s',
  },
  data(): {
    version: string;
    boundaryError: string;
    fadeElems: boolean;
    me: TODO | null;
  } {
    return {
      version: APP_VERSION,
      boundaryError: '',
      fadeElems: false,
      me: null,
    };
  },
  setup(_, { root }) {
    const scale = ref(getBackgroundScale());

    const { isOnline } = useNetwork();

    const mainStore = useMainStore();
    const authStore = useAuthStore();

    const pauseGql = computed(
      () => !isOnline.value || !authStore.isAuthenticated,
    );

    watch(
      pauseGql,
      (value) => {
        switch (value) {
          case true:
            QueueLink.close();
            break;
          case false:
          default:
            QueueLink.open();
            break;
        }
      },
      { immediate: true },
    );

    useEventListener('resize', () => (scale.value = getBackgroundScale()));

    function getBackgroundScale() {
      const windowWidth = document.documentElement.clientWidth;
      const windowHeight = document.documentElement.clientHeight;

      let calculatedScale = Math.min(windowWidth / 1280, windowHeight / 960);
      // only scale down?
      if (calculatedScale > 1.2) calculatedScale = 1.2;
      return calculatedScale;
    }

    const { onResult: onMeAuthResult, onError: onMeAuthError } = useQuery(
      MeAuthDocument,
      undefined,
      {
        fetchPolicy: 'network-only',
        enabled: computed(
          () => authStore.isAuthenticated && !authStore.isTransitioning,
        ),
      },
    );

    onMeAuthResult((result) => {
      Sentry.setUser({
        id: result.data.me.id,
        username: result.data.me.username,
      });

      if (
        authStore.role !== 'DEMO_STUDENT' &&
        !result.data.me.typetuin.gamename &&
        !root.$route.path.startsWith('/introduction')
      ) {
        root.$router.push({ name: 'Introduction' });
      }
    });

    onMeAuthError(() => {
      Sentry.setUser(null);
    });

    const { onResult: onMyKeyboardLayoutResult } = useQuery(
      MyKeyboardLayoutDocument,
      undefined,
      {
        fetchPolicy: 'network-only',
        enabled: computed(
          () => authStore.isAuthenticated && !authStore.isTransitioning,
        ),
      },
    );

    onMyKeyboardLayoutResult((result) => {
      mainStore.keyboardLayout = result.data.me.keyboardLayout;
    });

    return { scale, mainStore, authStore, pauseGql };
  },
  created() {
    // Set initial locale
    this.$i18n.locale = this.mainStore.locale;
  },
  async mounted() {
    logger.info('Typetuin Game version', this.version);

    setGraphqlErrorHandler(this.handleGraphqlError);
  },
  computed: {
    hasDemoNotch(): boolean {
      return !!this.demoNotchPosition;
    },
    demoNotchPosition(): string | undefined {
      if (
        !(this.authStore.role === 'DEMO_STUDENT') ||
        this.$route.meta.allowUnauthorized
      )
        return;

      if (this.$route.meta.demoNotchPosition)
        return this.$route.meta.demoNotchPosition;

      return 'top';
    },
    focusMode(): boolean {
      if (!this.me || !this.me.typetuin) return false;
      return (
        this.me.typetuin.focusMode &&
        ['Level Game', 'Exam Examination', 'Test Exam Examination'].includes(
          this.$route.name,
        )
      );
    },
    backgroundImage(): TODO | undefined {
      // use props to replace values in dynamicBackgroundImage
      if (
        this.$route.meta &&
        this.$route.meta.background &&
        this.$route.meta.background.dynamicBackgroundImage
      ) {
        const route = Object.keys(this.$route.params).reduce((prev, key) => {
          if (
            this.$route.meta.background.dynamicBackgroundImage.includes(
              `[${key}]`,
            )
          ) {
            return this.$route.meta.background.dynamicBackgroundImage.replace(
              `[${key}]`,
              this.$route.params[key],
            );
            // return prev;
          }
          return prev;
        }, '');
        return route;
      }

      if (
        !this.$route.meta ||
        !this.$route.meta.background ||
        !this.$route.meta.background.backgroundImage
      ) {
        return undefined;
      }
      return this.$route.meta.background.backgroundImage;
    },
    backgroundOpacity(): TODO | undefine {
      if (
        !this.$route.meta ||
        !this.$route.meta.background ||
        !this.$route.meta.background.backgroundOpacity
      ) {
        return undefined;
      }
      return this.$route.meta.background.backgroundOpacity;
    },
    routerClass(): TODO | undefined {
      if (!this.$route.meta || !this.$route.meta.routerClass) return undefined;
      return this.$route.meta.routerClass;
    },
  },
  watch: {
    backgroundOpacity(value) {
      if (value < 0.5) {
        this.fadeElems = true;
      } else {
        this.fadeElems = false;
      }
    },
    '$i18n.locale'(newValue) {
      // Sync $i18n to mainStore
      if (newValue !== this.mainStore.locale) this.mainStore.locale = newValue;
    },
    'mainStore.locale'(newValue) {
      // Sync mainStore to $i18n
      if (newValue !== this.$i18n.locale) this.$i18n.locale = newValue;
    },
  },
  methods: {
    demoInfo() {
      this.$nextTick(() => {
        this.$modal.show(
          ModalMessage,
          {
            title: 'Welkom in de Typetuin!',
            bodyText:
              'Leuk dat je een kijkje komt nemen! In deze demoversie kun je level 1, level 8 en één bonuslevel spelen. In de demoversie zijn niet alle functionaliteiten beschikbaar en ook je voortgang wordt niet bijgehouden, maar je kunt rustig even rondkijken. Wil je meer weten, ga dan naar <a target="_blank" href="https://typetuin.nl/aanmelden">typetuin.nl</a>',
            primaryText: 'Doorgaan',
          },
          {
            width: 600,
            height: 'auto',
          },
        );
      });
    },
    handleGraphqlError(error: ErrorResponse): void {
      logger.getLogger('GraphQL').error(error);

      if (
        error.operation.operationName === 'Refresh' &&
        error.graphQLErrors?.[0].message === 'Unauthorized'
      ) {
        this.authStore.logout('cookie').then(() => window.location.reload());
        return;
      }

      if (error.graphQLErrors) {
        const actions = error.graphQLErrors.map((graphQLError) => {
          switch (graphQLError.message) {
            case 'Refresh token is missing':
            case 'Invalid Refresh Token':
              this.authStore
                .logout('cookie')
                .then(() => window.location.reload());
              return true;
            case 'Unauthorized':
              return 'refresh';
            case 'Invalid CSRF-token':
            case 'Missing CSRF-token (hash)':
              return 'refresh-csrf';
            case 'Rate limit exceeded':
              this.$modal.show(
                ModalMessage,
                {
                  title: 'Maximum aantal proeflessen vandaag bereikt.',
                  bodyText:
                    'Uit veiligheidsoverwegingen kun je per IP adres maximaal 5 proeflessen per dag doen.',
                  primaryText: 'OK',
                  primaryIcon: false,
                },
                {
                  height: 350,
                },
              );
              return true;
            case 'Invalid Credentials':
              this.$modal.show(
                ModalMessage,
                {
                  title: 'Oeps.',
                  bodyText:
                    'Je gebruikersnaam en/of wachtwoord zijn niet correct. Vul je juiste gebruikersnaam en wachtwoord in.',
                  primaryText: 'OK',
                  primaryIcon: false,
                },
                {
                  height: 350,
                },
              );
              return true;
            case 'Password not set':
              this.$modal.show(
                ModalMessage,
                {
                  title: 'Registratie is nog niet voltooid',
                  bodyText:
                    'Voltooi eerst de stappen in de registratie e-mail die je ontvangen hebt, of vraag een beheerder je wachtwoord in te stellen.',
                  primaryText: 'OK',
                  primaryIcon: false,
                },
                {
                  height: 350,
                },
              );
              return true;
            case 'Temp Password Exception':
              this.$modal.show(
                ModalMessage,
                {
                  title: 'Tijdelijk wachtwoord',
                  bodyText:
                    'Je hebt een tijdelijk wachtwoord. Je wordt naar een pagina geleid om dit naar een wachtwoord naar keuze te veranderen.',
                  primaryText: 'OK',
                  primaryIcon: false,
                  primaryCallback() {
                    this.$router.push({
                      name: 'NewPassword',
                      query: {
                        token: graphQLError.extensions.exception.response.token,
                        user_id:
                          graphQLError.extensions.exception.response.userId,
                      },
                    });
                  },
                },
                {
                  width: 600,
                  height: 'auto',
                },
              );
              return true;
            case `Expected password reset token and it's expiry date to be set`:
              this.$modal.show(
                ModalMessage,
                {
                  title: 'Wachtwoord reset',
                  bodyText:
                    'Je wachtwoord reset is verlopen. Je wordt naar een pagina geleid om opnieuw je wachtwoord te resetten.',
                  primaryText: 'OK',
                  primaryIcon: false,
                  primaryCallback() {
                    this.$router.push({ name: 'PasswordReset' });
                  },
                },
                {
                  width: 600,
                  height: 'auto',
                },
              );
              return true;

            default:
              logger
                .getLogger('GraphQL')
                .error('Unhandled GraphQLError', graphQLError);
              return false;
          }
        });

        if (actions.includes('refresh')) {
          logger.getLogger('GraphQL').trace('Refresh');
          return fromPromise(this.authStore.refresh()).flatMap(() =>
            error.forward(error.operation),
          );
        }

        if (actions.includes('refresh-csrf')) {
          logger.getLogger('GraphQL').trace('Refresh CSRF');
          return fromPromise(refreshCsrf()).flatMap(() =>
            error.forward(error.operation),
          );
        }

        if (actions.every((value) => value === true)) {
          return;
        }

        const status =
          error.graphQLErrors[0].extensions &&
          error.graphQLErrors[0].extensions.exception
            ? error.graphQLErrors[0].extensions.exception.status
            : null;
        const { query, code, exception } = error.graphQLErrors[0].extensions;
        const { message } = error.graphQLErrors[0];
        const errorCode = exception.response.error;

        logger.debug(error.graphQLErrors[0].extensions);
        logger.debug(
          'error message:',
          message,
          'error status',
          status,
          'code',
          code,
          'error code',
          errorCode,
        );

        logger.debug({
          code,
          errorCode,
        });

        const errorMap = {
          // START_DATE_IN_FUTURE: 'De startdatum van de cursus ligt in de toekomst. Probeer het later nog eens.',
          END_DATE_IN_PAST:
            'De eindatum van je cursus is verstreken. Je kunt niet meer inloggen.',
          NO_LICENSE:
            'Er is op dit moment geen licentie gekoppeld aan je account. Neem contact op met de beheerder.',
          SCHOOL_DEACTIVATED: 'De school is niet meer actief',
        };

        if (errorCode === 'START_DATE_IN_FUTURE') {
          logger.debug(error);
          return this.$modal.show(
            ModalMessage,
            {
              title: 'Startdatum in de toekomst.',
              bodyText: `De startdatum van de cursus ligt in de toekomst. Je kunt inloggen vanaf${
                exception.startDate
                  ? ` ${date(new Date(exception.startDate))}`
                  : ''
              }.`,
              primaryText: 'OK',
              primaryIcon: false,
            },
            {
              height: 350,
            },
          );
        }

        if (errorMap[errorCode]) {
          return this.showErrorModal(errorMap[errorCode]);
        }
        if (errorMap[code]) {
          return this.showErrorModal(errorMap[code]);
        }

        if (message === 'Invalid token') {
          return this.showErrorModal(
            'Het lijkt er op dat deze token verlopen is of al gebruikt is. Als er al een wachtwoord is aangemaakt kun je hiermee inloggen. Indien je nog geen wachtwoord hebt dan kun je een nieuw wachtwoord aanvragen via de "wachtwoord vergeten?" link op de inlogpagina. Als er nog steeds een probleem zich voordoet neem dan contact op met Typetuin.',
          );
        }

        if (message === 'Invalid license') {
          return this.showErrorModal(
            'Het lijkt er op dat je geen gekoppelde licentie hebt, of dat deze is verlopen of nog niet is begonnen. Neem contact op met je school of met Typetuin.',
          );
        }

        if (status === 403) {
          if (message.includes('Not allowed just yet')) {
            const date = message.split('date: ')[1];
            return this.showErrorModal(
              `Je account is nog niet actief, vanaf ${date(
                new Date(date),
                'dd-MM-yyyy',
              )} kun je inloggen`,
              false,
            );
          }
        }

        logger.error(message);

        if (
          message ===
          'Unexpected error value: "The challenge request has been expired."'
        ) {
          return false;
        }

        switch (status) {
          case 404:
            if (query === '{me{__typename id}}') {
              logger.debug(
                'User not found. This can happen when you have a valid token, but the user is not in current database',
              );
            }
          // eslint-disable-next-line no-fallthrough
          default:
            return this.showErrorModal(message);
        }
      }

      if (error.networkError) {
        if (canRetryForever(error.operation.operationName)) {
          return;
        }

        logger.error('Network error', {
          error: JSON.stringify(error),
          networkError: JSON.stringify(error.networkError),
          operation: JSON.stringify(error.operation),
          operationName: error.operation.operationName,
          variables: JSON.stringify(error.operation.variables),
        });

        if (error.networkError.message === 'Failed to fetch') {
          logger.info('Network error: Failed to fetch');
          return this.showErrorModal(
            'Het lijkt erop dat er geen connectie kan worden gemaakt met de server. Controleer uw internetverbinding en probeer het opnieuw.',
          );
        }

        if (RegExp(/5[0-9][0-9]/).test(String(error.networkError.statusCode))) {
          logger.info('Network error: 5xx');
          return this.showErrorModal(
            `Het lijkt erop dat er problemen zijn met de server. Probeer het later nog eens. ${error.networkError}`,
          );
        }

        return this.showErrorModal(error.networkError.message);
      }

      return false;
    },
    showErrorModal(message, showContactInfo = true) {
      this.$modal.hide('errorModal');
      // this.boundaryError = message;
      this.$modal.show(
        ModalError,
        {
          error: message,
          showContactInfo,
        },
        {
          width: 600,
          height: 'auto',
          name: 'errorModal',
        },
      );
    },
    handleBoundaryError(error) {
      logger.getLogger('Boundary').error(error);

      // Handle GraphQL and Network errors seperately in graphqlErrorHandler
      if (
        error.err.message.includes('GraphQL error') ||
        error.err.message.includes('Network error') ||
        error.err.message ===
          'Store reset while query was in flight (not completed in link chain)'
      ) {
        return;
      }

      this.showErrorModal(error.err);
    },
  },
});
